/**
 * Google Places API Text Search utility
 * Implements the new Google Places API Text Search endpoint
 */

// Expo inlines EXPO_PUBLIC_* from .env; web builds use webpack DefinePlugin.
const GOOGLE_PLACES_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_PLACES_API_KEY;
const GOOGLE_PLACES_API_URL = 'https://places.googleapis.com/v1/places:searchText';
const GOOGLE_PLACE_DETAILS_API_URL = 'https://places.googleapis.com/v1/places';

// Validate API key is present
if (!GOOGLE_PLACES_API_KEY) {
  console.error('⚠️  Google Places API key is missing!');
  console.error('   Make sure EXPO_PUBLIC_GOOGLE_PLACES_API_KEY is set in your environment variables.');
  console.error('   For web builds on Vercel, set it in the Vercel dashboard.');
  console.error('   For native builds, set it in eas.json or .env file.');
}

/**
 * Enhance search query for better partial matching
 * @param {string} query - Original search query
 * @returns {string} Enhanced search query
 */
const enhanceSearchQuery = (query) => {
  if (!query || query.length < 2) {
    return query;
  }

  // For short queries, add common restaurant-related terms to improve matching
  if (query.length <= 6) {
    // Don't modify if it already contains restaurant-related terms
    const restaurantTerms = ['restaurant', 'cafe', 'bar', 'food', 'dining', 'eatery', 'bistro'];
    const hasRestaurantTerm = restaurantTerms.some(term =>
      query.toLowerCase().includes(term)
    );

    if (!hasRestaurantTerm) {
      // Add "restaurant" to help with partial name matching
      return `${query} restaurant`;
    }
  }

  return query;
};

/**
 * Search for places using Google Places API Text Search
 * @param {string} textQuery - The search query (e.g., "pizza in New York")
 * @param {Object} options - Additional search options
 * @param {Object} options.locationBias - Location bias object with lat/lng
 * @param {string} options.languageCode - Language code (default: 'en')
 * @param {number} options.maxResultCount - Maximum number of results (default: 20)
 * @param {string} options.rankPreference - Ranking preference ('DISTANCE' or 'RELEVANCE')
 * @returns {Promise<Array>} Array of place objects
 */
export const searchPlaces = async (textQuery, options = {}) => {
  try {
    if (!GOOGLE_PLACES_API_KEY) {
      throw new Error('Google Places API key is not configured. Please set EXPO_PUBLIC_GOOGLE_PLACES_API_KEY in your environment variables.');
    }

    if (!textQuery || textQuery.trim().length === 0) {
      return [];
    }

    const {
      locationBias,
      languageCode = 'en',
      maxResultCount = 20,
      rankPreference = 'RELEVANCE', // Default to relevance for better partial matches
      /** When true, use the query as-is (e.g. House Pick name + address matching). */
      skipQueryEnhancement = false,
    } = options;

    // Enhance search query for better partial matching
    const enhancedQuery = skipQueryEnhancement
      ? textQuery.trim()
      : enhanceSearchQuery(textQuery.trim());

    // Build the request body
    const requestBody = {
      textQuery: enhancedQuery,
      maxResultCount,
      languageCode,
      rankPreference
    };

    // Add location bias if provided
    if (locationBias && locationBias.latitude && locationBias.longitude) {
      requestBody.locationBias = {
        circle: {
          center: {
            latitude: locationBias.latitude,
            longitude: locationBias.longitude
          }
        }
      };
    }

    // Define the fields we want to retrieve (using only basic supported fields)
    const fieldMask = [
      'places.displayName',
      'places.formattedAddress',
      'places.shortFormattedAddress',
      'places.location',
      'places.types',
      'places.priceLevel',
      'places.rating',
      'places.userRatingCount',
      'places.id'
    ].join(',');

    const response = await fetch(GOOGLE_PLACES_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
        'X-Goog-FieldMask': fieldMask
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Google Places API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });
      throw new Error(`Google Places API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    //
    // Filter and transform the response to match our app's expected format
    const places = data.places || [];

    // Filter for restaurant/food related places, clubs, and other allowed types
    const restaurantTypes = [
      'restaurant',
      'bar',
      'cafe',
      'bakery',
      'food',
      'meal_takeaway',
      'meal_delivery',
      'coffee_shop',
      'fast_food_restaurant',
      'american_restaurant',
      'mexican_restaurant',
      'italian_restaurant',
      'japanese_restaurant',
      'chinese_restaurant',
      'asian_restaurant',
      'mediterranean_restaurant',
      'seafood_restaurant',
      'steak_house',
      'pizza_restaurant',
    ];
    const barTypes = [
      'pub',
      'sports_bar',
      'irish_pub',
      'bar_and_grill',
      'night_club',
      'cocktail_bar',
      'wine_bar',
      'brewery',
      'distillery',
      'lounge',
      'beer_garden',
      'tasting_room',
      'rooftop_bar',
      'pool_bar',
      'beach_bar',
    ];
    const specialtyFoodTypes = [
      'tea_house',
      'bubble_tea_shop',
      'dessert_shop',
      'ice_cream_shop',
      'juice_shop',
      'wine_store',
      'wine_shop',
      'food_court',
      'market',
    ];
    const venueTypes = [
      'hotel',
      'resort',
      'event_venue',
      'live_music_venue',
      'music_venue',
      'concert_hall',
    ];
    const generalTypes = [
      'establishment',
      'point_of_interest',
    ];
    const allowedTypes = [...new Set([
      ...restaurantTypes,
      ...barTypes,
      ...specialtyFoodTypes,
      ...venueTypes,
      ...generalTypes,
    ])]; // Remove duplicates
    const filteredPlaces = places.filter(place => {
      const types = place.types || [];
      return types.some(type => allowedTypes.includes(type));
    });

    return filteredPlaces.map(place => ({
      id: place.id,
      name: place.displayName?.text || 'Unknown Place',
      address: place.formattedAddress || place.shortFormattedAddress || '',
      latitude: place.location?.latitude || 0,
      longitude: place.location?.longitude || 0,
      types: place.types || [],
      primaryType: place.types?.[0] || 'restaurant',
      priceLevel: place.priceLevel || null,
      rating: place.rating || null,
      userRatingCount: place.userRatingCount || 0,
      photos: [], // Not included in basic field mask
      websiteUri: null, // Not included in basic field mask
      phoneNumber: null, // Not included in basic field mask
      businessStatus: 'OPERATIONAL', // Default value
      isGooglePlace: true, // Flag to identify Google Places results
      distance: null // Will be calculated by the calling component
    }));

  } catch (error) {
    console.error('Error searching places:', error);
    throw error;
  }
};

const normalizeOpeningHours = (placeDetails) => {
  const weekdayDescriptions =
    placeDetails?.regularOpeningHours?.weekdayDescriptions ||
    placeDetails?.currentOpeningHours?.weekdayDescriptions ||
    [];

  if (!Array.isArray(weekdayDescriptions) || weekdayDescriptions.length === 0) {
    return null;
  }

  const normalizedHours = weekdayDescriptions
    .map((entry) => {
      if (typeof entry !== 'string') return null;
      const separatorIndex = entry.indexOf(':');
      if (separatorIndex <= 0) return null;
      return {
        days: entry.slice(0, separatorIndex).trim(),
        times: entry.slice(separatorIndex + 1).trim(),
      };
    })
    .filter(Boolean);

  return normalizedHours.length > 0 ? normalizedHours : null;
};

/** In-memory cache so sheet opens don't re-hit Place Details every time. */
const placeDetailsCache = new Map();
/** Bump when Place Details payload shape changes so stale in-memory entries re-fetch. */
const PLACE_DETAILS_CACHE_VERSION = 6;

const emptyPlaceDetails = () => ({
  address: null,
  openingHours: null,
  photoUrls: [],
  phoneNumber: null,
  websiteUri: null,
  openNow: null,
  price: null,
  editorialSummary: null,
  _v: PLACE_DETAILS_CACHE_VERSION,
});

/**
 * Convert Google Places priceLevel enum/number to a $ display string.
 * @param {number|string|null|undefined} priceLevel
 * @returns {string|null}
 */
export const formatPriceLevel = (priceLevel) => {
  if (priceLevel === null || priceLevel === undefined) return null;

  let level = priceLevel;
  if (typeof level === 'string') {
    const priceLevelMap = {
      PRICE_LEVEL_FREE: 0,
      PRICE_LEVEL_INEXPENSIVE: 1,
      PRICE_LEVEL_MODERATE: 2,
      PRICE_LEVEL_EXPENSIVE: 3,
      PRICE_LEVEL_VERY_EXPENSIVE: 4,
    };
    if (!(level in priceLevelMap)) return null;
    level = priceLevelMap[level];
  }

  if (typeof level !== 'number' || level < 0 || level > 4) return null;
  const levels = ['', '$', '$$', '$$$', '$$$$'];
  return levels[level] || null;
};

/**
 * Format a Google Money object for display (e.g. units "10" → "$10").
 * @param {{ currencyCode?: string, units?: string|number, nanos?: number }|null|undefined} money
 * @returns {string|null}
 */
const formatMoneyAmount = (money) => {
  if (!money || (money.units == null && money.nanos == null)) return null;
  const units = Number(money.units ?? 0);
  const nanos = Number(money.nanos ?? 0);
  if (!Number.isFinite(units) && !Number.isFinite(nanos)) return null;
  const amount = units + (Number.isFinite(nanos) ? nanos / 1e9 : 0);
  const currencyCode = typeof money.currencyCode === 'string' ? money.currencyCode : 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      minimumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${Math.round(amount)}`;
  }
};

/**
 * Convert Google Places priceRange to a display string (e.g. "$10–20").
 * Falls back to null when range data is missing.
 * @param {{ startPrice?: object, endPrice?: object }|null|undefined} priceRange
 * @returns {string|null}
 */
export const formatPriceRange = (priceRange) => {
  if (!priceRange || typeof priceRange !== 'object') return null;
  const start = formatMoneyAmount(priceRange.startPrice);
  const end = formatMoneyAmount(priceRange.endPrice);
  if (start && end) {
    // "$10–$20" → "$10–20" when same currency symbol prefix
    if (start[0] === end[0] && !/\d/.test(start[0])) {
      return `${start}–${end.slice(1)}`;
    }
    return `${start}–${end}`;
  }
  if (start) return `${start}+`;
  if (end) return `Up to ${end}`;
  return null;
};

/**
 * Fetch address, opening hours, photos, phone, website, and open-now from Google Place Details.
 * Prefer this over DB `address` / `openingHours` (deprecated).
 * @param {string} placeId - Google Place ID
 * @returns {Promise<{
 *   address: string|null,
 *   openingHours: Array<{days: string, times: string}>|null,
 *   photoUrls: string[],
 *   phoneNumber: string|null,
 *   websiteUri: string|null,
 *   openNow: boolean|null,
 *   price: string|null,
 * }>}
 */
export const fetchPlaceDetails = async (placeId) => {
  const empty = emptyPlaceDetails();
  const normalizedId = normalizePlaceId(placeId);
  if (!GOOGLE_PLACES_API_KEY || !normalizedId) {
    return empty;
  }

  if (placeDetailsCache.has(normalizedId)) {
    const cached = placeDetailsCache.get(normalizedId);
    const hasStaleMediaUrls =
      Array.isArray(cached?.photoUrls) &&
      cached.photoUrls.some(
        (url) => typeof url === 'string' && url.includes('places.googleapis.com')
      );
    if (
      cached?._v === PLACE_DETAILS_CACHE_VERSION &&
      Array.isArray(cached?.photoUrls) &&
      !hasStaleMediaUrls
    ) {
      return cached;
    }
  }

  try {
    const response = await fetch(`${GOOGLE_PLACE_DETAILS_API_URL}/${encodeURIComponent(normalizedId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
        'X-Goog-FieldMask': [
          'formattedAddress',
          'regularOpeningHours.weekdayDescriptions',
          'currentOpeningHours.weekdayDescriptions',
          'currentOpeningHours.openNow',
          'nationalPhoneNumber',
          'internationalPhoneNumber',
          'websiteUri',
          'priceLevel',
          'priceRange',
          'editorialSummary',
          'photos',
        ].join(','),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Google Place Details error:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
      });
      return empty;
    }

    const placeDetails = await response.json();
    const address =
      typeof placeDetails?.formattedAddress === 'string' && placeDetails.formattedAddress.trim()
        ? placeDetails.formattedAddress.trim()
        : null;
    const phoneNumber =
      (typeof placeDetails?.nationalPhoneNumber === 'string' &&
        placeDetails.nationalPhoneNumber.trim()) ||
      (typeof placeDetails?.internationalPhoneNumber === 'string' &&
        placeDetails.internationalPhoneNumber.trim()) ||
      null;
    const websiteUri =
      typeof placeDetails?.websiteUri === 'string' && placeDetails.websiteUri.trim()
        ? placeDetails.websiteUri.trim()
        : null;
    const openNowRaw = placeDetails?.currentOpeningHours?.openNow;
    const openNow = typeof openNowRaw === 'boolean' ? openNowRaw : null;
    const price =
      formatPriceRange(placeDetails?.priceRange) ||
      formatPriceLevel(placeDetails?.priceLevel);
    const editorialSummary =
      typeof placeDetails?.editorialSummary?.text === 'string' &&
      placeDetails.editorialSummary.text.trim()
        ? placeDetails.editorialSummary.text.trim()
        : null;
    const photoUrls = await resolvePhotoUrlsFromPlacePhotos(placeDetails.photos, 8);
    const result = {
      address,
      openingHours: normalizeOpeningHours(placeDetails),
      photoUrls,
      phoneNumber,
      websiteUri,
      openNow,
      price,
      editorialSummary,
      _v: PLACE_DETAILS_CACHE_VERSION,
    };
    placeDetailsCache.set(normalizedId, result);
    return result;
  } catch (error) {
    console.error('Error fetching place details:', error);
    return empty;
  }
};

/**
 * Fetch normalized opening hours for a place by Google Place ID.
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Array<{days: string, times: string}>|null>}
 */
export const fetchPlaceOpeningHours = async (placeId) => {
  const details = await fetchPlaceDetails(placeId);
  return details.openingHours;
};

/**
 * Normalize a Google Place ID for the Places API (New) path segment.
 * Accepts raw IDs (`ChIJ...`) or resource names (`places/ChIJ...`).
 * @param {string} placeId
 * @returns {string}
 */
const normalizePlaceId = (placeId) => {
  if (!placeId) return '';
  return placeId.startsWith('places/') ? placeId.slice('places/'.length) : placeId;
};

/**
 * Build a media URL for a Google Place photo resource name.
 * Prefer `resolvePlacePhotoUri` for React Native — the default media URL redirects
 * and often fails to render in `Image`.
 * @param {string} photoName - Photo resource name from Place Details (e.g. places/.../photos/...)
 * @param {Object} [options]
 * @param {number} [options.maxHeightPx=800]
 * @param {number} [options.maxWidthPx=800]
 * @returns {string|null}
 */
export const getPlacePhotoUrl = (photoName, options = {}) => {
  if (!GOOGLE_PLACES_API_KEY || !photoName) return null;
  const { maxHeightPx = 800, maxWidthPx = 800 } = options;
  const params = new URLSearchParams({
    maxHeightPx: String(maxHeightPx),
    maxWidthPx: String(maxWidthPx),
    key: GOOGLE_PLACES_API_KEY,
  });
  return `https://places.googleapis.com/v1/${photoName}/media?${params.toString()}`;
};

/**
 * Resolve a Google Place photo resource name to a direct image URI.
 * Uses skipHttpRedirect so RN/Expo Image can load googleusercontent URLs.
 * @param {string} photoName
 * @param {Object} [options]
 * @returns {Promise<string|null>}
 */
export const resolvePlacePhotoUri = async (photoName, options = {}) => {
  const mediaUrl = getPlacePhotoUrl(photoName, options);
  if (!mediaUrl) return null;
  try {
    const response = await fetch(`${mediaUrl}&skipHttpRedirect=true`);
    if (!response.ok) {
      console.error('Google Place photo media error:', response.status, photoName?.slice?.(0, 80));
      return null;
    }
    const data = await response.json().catch(() => null);
    return typeof data?.photoUri === 'string' && data.photoUri.trim()
      ? data.photoUri.trim()
      : null;
  } catch (error) {
    console.error('Error resolving place photo URI:', error);
    return null;
  }
};

/**
 * Resolve up to `limit` photo resource names to direct image URIs.
 * @param {Array<{name?: string}>} photos
 * @param {number} [limit=8]
 * @param {Object} [photoOptions] - Passed to `resolvePlacePhotoUri` (e.g. maxHeightPx/maxWidthPx)
 * @returns {Promise<string[]>}
 */
const resolvePhotoUrlsFromPlacePhotos = async (photos, limit = 8, photoOptions = {}) => {
  const names = (photos || [])
    .map((photo) => photo?.name)
    .filter(Boolean)
    .slice(0, limit);
  if (names.length === 0) return [];
  const urls = await Promise.all(
    names.map((name) => resolvePlacePhotoUri(name, photoOptions))
  );
  return urls.filter(Boolean);
};

/** In-memory cache for display-name + photo Place Details (explore list / Internal Tool). */
const placeDisplayCache = new Map();

/**
 * Fetch place name, address, and photo URLs from Google Places API (New) by Place ID.
 * Used by the Internal Tool card and explore-list enrichment; does not use any app database data.
 * @param {string} placeId - Google Place ID
 * @param {Object} [options]
 * @param {number} [options.maxHeightPx=800] - Photo height for media resolution
 * @param {number} [options.maxWidthPx=800] - Photo width for media resolution
 * @param {number} [options.photoLimit=8] - Max photos to resolve
 * @returns {Promise<{id: string, name: string, address: string, photoUrls: string[]}|null>}
 */
export const fetchPlaceDisplayDetails = async (placeId, options = {}) => {
  if (!GOOGLE_PLACES_API_KEY) {
    throw new Error(
      'Google Places API key is not configured. Please set EXPO_PUBLIC_GOOGLE_PLACES_API_KEY.'
    );
  }

  const normalizedId = normalizePlaceId(placeId);
  if (!normalizedId) return null;

  const { maxHeightPx = 800, maxWidthPx = 800, photoLimit = 8 } = options;
  const cacheKey = `${normalizedId}:${maxHeightPx}x${maxWidthPx}:${photoLimit}`;
  if (placeDisplayCache.has(cacheKey)) {
    return placeDisplayCache.get(cacheKey);
  }

  try {
    const response = await fetch(
      `${GOOGLE_PLACE_DETAILS_API_URL}/${encodeURIComponent(normalizedId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
          'X-Goog-FieldMask': ['id', 'displayName', 'formattedAddress', 'photos'].join(','),
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Google Place Details error:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        placeId: normalizedId,
      });
      throw new Error(
        `Google Places API error: ${response.status} - ${errorData.error?.message || response.statusText}`
      );
    }

    const place = await response.json();
    const photoUrls = await resolvePhotoUrlsFromPlacePhotos(place.photos, photoLimit, {
      maxHeightPx,
      maxWidthPx,
    });

    const result = {
      id: place.id || normalizedId,
      name: place.displayName?.text || 'Unknown Place',
      address: place.formattedAddress || '',
      photoUrls,
    };
    placeDisplayCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error fetching place display details:', error);
    throw error;
  }
};

/**
 * Search for nearby restaurants using current location
 * @param {Object} location - Current location with latitude and longitude
 * @param {string} query - Search query (optional)
 * @param {Object} options - Additional search options
 * @returns {Promise<Array>} Array of nearby restaurant objects
 */
export const searchNearbyRestaurants = async (location, query = '', options = {}) => {
  try {
    if (!location || !location.latitude || !location.longitude) {
      throw new Error('Valid location is required for nearby search');
    }

    const searchQuery = query.trim() || 'restaurants near me';

    return await searchPlaces(searchQuery, {
      locationBias: {
        latitude: location.latitude,
        longitude: location.longitude,
        radius: options.radius || 10000 // 10km default
      },
      maxResultCount: options.maxResults || 20,
      rankPreference: 'RANK_PREFERENCE_UNSPECIFIED',
      ...options
    });

  } catch (error) {
    console.error('Error searching nearby restaurants:', error);
    throw error;
  }
};

/**
 * Search for specific cuisine types
 * @param {string} cuisine - Cuisine type (e.g., "Italian", "Mexican", "Chinese")
 * @param {Object} location - Current location for location bias
 * @param {Object} options - Additional search options
 * @returns {Promise<Array>} Array of restaurant objects
 */
export const searchCuisine = async (cuisine, location, options = {}) => {
  try {
    const searchQuery = `${cuisine} restaurants`;

    return await searchPlaces(searchQuery, {
      locationBias: location ? {
        latitude: location.latitude,
        longitude: location.longitude,
        radius: options.radius || 25000 // 25km default
      } : undefined,
      maxResultCount: options.maxResults || 20,
      rankPreference: 'RANK_PREFERENCE_UNSPECIFIED',
      ...options
    });

  } catch (error) {
    console.error('Error searching cuisine:', error);
    throw error;
  }
};

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - First point latitude
 * @param {number} lon1 - First point longitude
 * @param {number} lat2 - Second point latitude
 * @param {number} lon2 - Second point longitude
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

const deg2rad = (deg) => {
  return deg * (Math.PI / 180);
};

/**
 * Format distance for display
 * @param {number} distanceInKm - Distance in kilometers
 * @param {'mi' | 'km'} [unit='mi'] - Display unit
 * @returns {string} Formatted distance string
 */
export const formatDistance = (distanceInKm, unit = 'mi') => {
  if (!distanceInKm && distanceInKm !== 0) return '';
  if (unit === 'km') {
    if (distanceInKm < 0.016) return 'Here'; // ~0.01 mi, matches "very close" threshold
    if (distanceInKm < 1) return `${Math.round(distanceInKm * 1000)} m`;
    return `${distanceInKm.toFixed(1)} km`;
  }
  const miles = distanceInKm * 0.621371; // Convert km to miles
  if (miles < 0.01) return 'Here';
  return `${miles.toFixed(1)} mi`;
};
